import React from 'react';
import {
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import type { StackScreenProps } from '@react-navigation/stack';
import { colors, fontSizes, fontWeights, radius, spacing } from '../theme';
import { SERVICES, TESTIMONIALS } from '../data/shopData';
import { ServiceCard } from '../components/ServiceCard';
import { TestimonialCard } from '../components/TestimonialCard';
import type { RootStackParamList } from '../navigation/AppNavigator';

type Props = StackScreenProps<RootStackParamList, 'Home'>;

const PERKS = [
  { icon: '📋', label: 'Free Estimates' },
  { icon: '🛡️', label: 'Lifetime Warranty' },
  { icon: '🤝', label: 'Insurance Accepted' },
];

export function HomeScreen({ navigation }: Props) {
  const { width } = useWindowDimensions();

  // Responsive columns for services grid
  const numColumns = width < 600 ? 1 : width < 960 ? 2 : 3;

  function handleBookPress(serviceId?: string) {
    navigation.navigate('Booking', { serviceId });
  }

  // Split services into rows for the grid
  const rows: typeof SERVICES[] = [];
  for (let i = 0; i < SERVICES.length; i += numColumns) {
    rows.push(SERVICES.slice(i, i + numColumns));
  }

  return (
    <SafeAreaView style={styles.safeArea} edges={['top']}>
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        {/* ── Nav Bar ─────────────────────────────────────────────── */}
        <View style={styles.navbar}>
          <View style={styles.logoRow}>
            <Text style={styles.logoIcon}>🔧</Text>
            <Text style={styles.logoText}>Apex Auto Body</Text>
          </View>
          <TouchableOpacity
            style={styles.navBookBtn}
            onPress={() => handleBookPress()}
            activeOpacity={0.8}
          >
            <Text style={styles.navBookBtnText}>Book Now</Text>
          </TouchableOpacity>
        </View>

        {/* ── Hero ────────────────────────────────────────────────── */}
        <View style={styles.hero}>
          <View style={styles.heroBadge}>
            <Text style={styles.heroBadgeText}>Serving the community since 1998</Text>
          </View>
          <Text style={styles.heroTitle}>Your Car Deserves{'\n'}Expert Care</Text>
          <Text style={styles.heroSubtitle}>
            From minor dents to major collision repair, Apex Auto Body & Collision
            delivers precision workmanship with a lifetime warranty on all repairs.
          </Text>
          <View style={styles.heroActions}>
            <TouchableOpacity
              style={styles.primaryBtn}
              onPress={() => handleBookPress()}
              activeOpacity={0.85}
            >
              <Text style={styles.primaryBtnText}>Book an Appointment</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.secondaryBtn}
              activeOpacity={0.75}
            >
              <Text style={styles.secondaryBtnText}>Get a Free Estimate</Text>
            </TouchableOpacity>
          </View>
          {/* Stats row */}
          <View style={styles.statsRow}>
            {[
              { value: '25+', label: 'Years of Service' },
              { value: '15K+', label: 'Cars Repaired' },
              { value: '4.9★', label: 'Average Rating' },
            ].map(stat => (
              <View key={stat.label} style={styles.statItem}>
                <Text style={styles.statValue}>{stat.value}</Text>
                <Text style={styles.statLabel}>{stat.label}</Text>
              </View>
            ))}
          </View>
        </View>

        {/* ── Services ─────────────────────────────────────────────── */}
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionLabel}>What We Do</Text>
            <Text style={styles.sectionTitle}>Our Services</Text>
            <Text style={styles.sectionSubtitle}>
              We handle everything from minor cosmetic repairs to full structural rebuilds,
              all backed by our industry-leading warranty.
            </Text>
          </View>
          <View style={styles.grid}>
            {rows.map((row, rowIdx) => (
              <View key={rowIdx} style={styles.gridRow}>
                {row.map(service => (
                  <View key={service.id} style={styles.gridCell}>
                    <ServiceCard
                      service={service}
                      onPress={() => handleBookPress(service.id)}
                    />
                  </View>
                ))}
                {/* Fill empty cells in last row */}
                {row.length < numColumns &&
                  Array.from({ length: numColumns - row.length }).map((_, i) => (
                    <View key={`empty-${i}`} style={styles.gridCell} />
                  ))}
              </View>
            ))}
          </View>
        </View>

        {/* ── Perks ────────────────────────────────────────────────── */}
        <View style={styles.perksSection}>
          <Text style={styles.perksSectionTitle}>Why Choose Apex?</Text>
          <View style={styles.perksRow}>
            {PERKS.map(perk => (
              <View key={perk.label} style={styles.perkItem}>
                <Text style={styles.perkIcon}>{perk.icon}</Text>
                <Text style={styles.perkLabel}>{perk.label}</Text>
              </View>
            ))}
          </View>
        </View>

        {/* ── Testimonials ─────────────────────────────────────────── */}
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionLabel}>Customer Reviews</Text>
            <Text style={styles.sectionTitle}>What Our Customers Say</Text>
          </View>
        </View>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.testimonialsRow}
        >
          {TESTIMONIALS.map(t => (
            <TestimonialCard key={t.id} testimonial={t} />
          ))}
        </ScrollView>

        {/* ── Footer CTA ────────────────────────────────────────────── */}
        <View style={styles.footerCta}>
          <Text style={styles.footerCtaTitle}>Ready to Get Started?</Text>
          <Text style={styles.footerCtaSubtitle}>
            Book your appointment online or call us at (555) 482-7700
          </Text>
          <TouchableOpacity
            style={styles.footerCtaBtn}
            onPress={() => handleBookPress()}
            activeOpacity={0.85}
          >
            <Text style={styles.footerCtaBtnText}>Book an Appointment →</Text>
          </TouchableOpacity>
        </View>

        {/* ── Footer ──────────────────────────────────────────────── */}
        <View style={styles.footer}>
          <Text style={styles.footerLogo}>🔧 Apex Auto Body & Collision</Text>
          <Text style={styles.footerAddress}>1428 Industrial Blvd · Springfield, IL 62701</Text>
          <Text style={styles.footerHours}>Mon–Fri 8AM–5PM · Sat 9AM–2PM</Text>
          <Text style={styles.footerCopyright}>© 2025 Apex Auto Body & Collision. All rights reserved.</Text>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: colors.background,
  },
  scroll: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },

  // Nav
  navbar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    backgroundColor: colors.surface,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  logoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  logoIcon: {
    fontSize: 22,
  },
  logoText: {
    fontSize: fontSizes.md,
    fontWeight: fontWeights.bold,
    color: colors.textPrimary,
  },
  navBookBtn: {
    backgroundColor: colors.accent,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: radius.pill,
  },
  navBookBtnText: {
    color: colors.white,
    fontSize: fontSizes.sm,
    fontWeight: fontWeights.semibold,
  },

  // Hero
  hero: {
    backgroundColor: colors.primary,
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.xxl,
    paddingBottom: spacing.xl,
    alignItems: 'center',
  },
  heroBadge: {
    backgroundColor: 'rgba(37, 99, 235, 0.3)',
    borderRadius: radius.pill,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
    marginBottom: spacing.md,
    borderWidth: 1,
    borderColor: 'rgba(37, 99, 235, 0.5)',
  },
  heroBadgeText: {
    color: '#93C5FD',
    fontSize: fontSizes.xs,
    fontWeight: fontWeights.medium,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  heroTitle: {
    fontSize: fontSizes.hero,
    fontWeight: fontWeights.extrabold,
    color: colors.white,
    lineHeight: 48,
    marginBottom: spacing.md,
    textAlign: 'center',
  },
  heroSubtitle: {
    fontSize: fontSizes.base,
    color: '#94A3B8',
    lineHeight: 24,
    marginBottom: spacing.xl,
    maxWidth: 520,
    textAlign: 'center',
  },
  heroActions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
    marginBottom: spacing.xl,
    justifyContent: 'center',
  },
  primaryBtn: {
    backgroundColor: colors.accent,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    borderRadius: radius.pill,
  },
  primaryBtnText: {
    color: colors.white,
    fontSize: fontSizes.base,
    fontWeight: fontWeights.semibold,
  },
  secondaryBtn: {
    backgroundColor: 'transparent',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    borderRadius: radius.pill,
    borderWidth: 1.5,
    borderColor: 'rgba(255,255,255,0.25)',
  },
  secondaryBtnText: {
    color: colors.white,
    fontSize: fontSizes.base,
    fontWeight: fontWeights.medium,
  },
  statsRow: {
    flexDirection: 'row',
    gap: spacing.xl,
    marginTop: spacing.sm,
    justifyContent: 'center',
  },
  statItem: {
    alignItems: 'center',
  },
  statValue: {
    fontSize: fontSizes.xl,
    fontWeight: fontWeights.extrabold,
    color: colors.white,
  },
  statLabel: {
    fontSize: fontSizes.xs,
    color: '#94A3B8',
    marginTop: 2,
  },

  // Sections
  section: {
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.xxl,
  },
  sectionHeader: {
    marginBottom: spacing.lg,
  },
  sectionLabel: {
    fontSize: fontSizes.xs,
    fontWeight: fontWeights.semibold,
    color: colors.accent,
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: spacing.xs,
  },
  sectionTitle: {
    fontSize: fontSizes.xxl,
    fontWeight: fontWeights.bold,
    color: colors.textPrimary,
    marginBottom: spacing.sm,
  },
  sectionSubtitle: {
    fontSize: fontSizes.base,
    color: colors.textSecondary,
    lineHeight: 24,
    maxWidth: 560,
  },

  // Services grid
  grid: {
    gap: spacing.md,
  },
  gridRow: {
    flexDirection: 'row',
    gap: spacing.md,
  },
  gridCell: {
    flex: 1,
  },

  // Perks
  perksSection: {
    backgroundColor: colors.surfaceAlt,
    marginTop: spacing.xxl,
    paddingVertical: spacing.xl,
    paddingHorizontal: spacing.lg,
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: colors.border,
  },
  perksSectionTitle: {
    fontSize: fontSizes.xxl,
    fontWeight: fontWeights.bold,
    color: colors.textPrimary,
    textAlign: 'center',
    marginBottom: spacing.lg,
  },
  perksRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: spacing.xl,
    flexWrap: 'wrap',
  },
  perkItem: {
    alignItems: 'center',
    gap: spacing.sm,
    minWidth: 100,
  },
  perkIcon: {
    fontSize: 36,
  },
  perkLabel: {
    fontSize: fontSizes.md,
    fontWeight: fontWeights.semibold,
    color: colors.textPrimary,
    textAlign: 'center',
  },

  // Testimonials
  testimonialsRow: {
    paddingHorizontal: spacing.lg,
    paddingBottom: spacing.md,
  },

  // Footer CTA
  footerCta: {
    backgroundColor: colors.accent,
    margin: spacing.lg,
    borderRadius: radius.xl,
    padding: spacing.xl,
    alignItems: 'center',
    marginTop: spacing.xxl,
  },
  footerCtaTitle: {
    fontSize: fontSizes.xl,
    fontWeight: fontWeights.bold,
    color: colors.white,
    textAlign: 'center',
    marginBottom: spacing.sm,
  },
  footerCtaSubtitle: {
    fontSize: fontSizes.base,
    color: 'rgba(255,255,255,0.8)',
    textAlign: 'center',
    marginBottom: spacing.lg,
  },
  footerCtaBtn: {
    backgroundColor: colors.white,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    borderRadius: radius.pill,
  },
  footerCtaBtnText: {
    color: colors.accent,
    fontSize: fontSizes.base,
    fontWeight: fontWeights.semibold,
  },

  // Footer
  footer: {
    backgroundColor: colors.primary,
    padding: spacing.xl,
    alignItems: 'center',
    gap: spacing.sm,
    marginTop: spacing.md,
  },
  footerLogo: {
    fontSize: fontSizes.md,
    fontWeight: fontWeights.bold,
    color: colors.white,
  },
  footerAddress: {
    fontSize: fontSizes.sm,
    color: '#94A3B8',
  },
  footerHours: {
    fontSize: fontSizes.sm,
    color: '#94A3B8',
  },
  footerCopyright: {
    fontSize: fontSizes.xs,
    color: '#475569',
    marginTop: spacing.sm,
  },
});
